/**
 * A-GTCC-VIEW-PSET-DZ1 — read client for the view "Danger Zone" (`dz`),
 * reference `docs/design/GTCC_REFERENCE_2026-09-01.html:886-899`.
 *
 * The view has exactly ONE source: the purge-job history
 * `GET /v1/admin/tenants/purge-jobs`, which
 * `views/purge/GtccPurgeView.tsx:104` already reads and which the reference
 * panel "Letzte Purge-Jobs" prints.
 *
 * The "Purge Center" panel above it fetches nothing at all. Its three rows are
 * labels that point at the EXISTING flows (Purge Center preview → typed
 * confirmation → countdown → execute, the DSGVO device purge, the session
 * revoke); none of them carries a request. That is the hard boundary of this
 * package: the delete, purge and revoke paths are not touched, not re-entered
 * and not re-implemented — only linked.
 *
 * Read-only by construction: `gtccGet` only, no mutation verb.
 */
import { gtccGet, type GtccResult } from "./gtcc-result.js";

/* ---------------------------------------------------------------- paths */

export const DZ_PURGE_JOBS_PATH = "/v1/admin/tenants/purge-jobs";

/* ----------------------------------------------------------- query keys */

/**
 * Its own key, NOT the `["gtcc", "purge", "jobs"]` of `GtccPurgeView`. Same
 * route, but not the same cached value: that view stores the raw
 * `PurgeJobsResponse`, this read stores the {@link GtccResult} envelope of
 * `gtccGet`. Sharing the key would mean whichever view mounts first decides
 * which shape the other one finds — and the other one then reads a payload it
 * cannot parse. One key, one shape.
 */
export const DZ_PURGE_JOBS_KEY = ["gtcc", "dz", "purge-jobs"] as const;

/* ---------------------------------------------------------------- types */

export const DZ_PURGE_JOB_STATES = [
  "pending",
  "previewed",
  "running",
  "succeeded",
  "partial_failed",
  "failed",
] as const;
export type DzPurgeJobState = (typeof DZ_PURGE_JOB_STATES)[number];

export interface DzPurgeJobDto {
  readonly id: string;
  readonly tenantDisplayLabel: string;
  readonly state: string;
  readonly actorEmail: string;
  readonly previewedAt: string;
}

export interface DzPurgeJobsDto {
  readonly items: readonly DzPurgeJobDto[];
}

/* --------------------------------------------------------------- reads */

export function fetchDzPurgeJobs(signal?: AbortSignal): Promise<GtccResult<DzPurgeJobsDto>> {
  return gtccGet<DzPurgeJobsDto>(DZ_PURGE_JOBS_PATH, signal);
}

/* -------------------------------------------------------------- verdict */

/**
 * Row verdict of the "Letzte Purge-Jobs" table.
 *
 * The reference draws a finished job green and a merely previewed one blue
 * (lines 896-897). A failed job is the one case the reference does not show
 * and that must never be drawn as either: it is red.
 *
 * An unknown state from the server reads `unmeasurable` rather than being
 * guessed into one of the buckets — Owner decision P10.
 */
export function dzPurgeJobVerdict(state: string): "ok" | "warn" | "crit" | "info" | "unmeasurable" {
  switch (state) {
    case "succeeded":
      return "ok";
    case "partial_failed":
      return "warn";
    case "failed":
      return "crit";
    case "pending":
    case "previewed":
    case "running":
      return "info";
    default:
      return "unmeasurable";
  }
}

/**
 * The reference masks the actor of a purge job (`info@geletec.de` stays, the
 * local part of anything longer is shortened). Masking happens here so the
 * view cannot accidentally print a full address into a screenshot that is
 * committed to the repository.
 *
 * Nothing is invented: an address that cannot be parsed is returned as the
 * literal "—" rather than as a guessed mask.
 */
export function dzMaskActor(email: string): string {
  const at = email.indexOf("@");
  if (at <= 0 || at === email.length - 1) return "—";
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (local.length <= 4) return `${local}@${domain}`;
  return `${local.slice(0, 2)}…${local.slice(-1)}@${domain}`;
}
